import React, { useEffect, useRef } from 'react';
import { TOTPVerificationForm } from '../../../shared/totpVerificationForm/TOTPVerificationForm';
import { useRecoverAccountMutation, useResetPasswordVerifyTotpMutation } from '../../../slices/auth-api.slice';
import { useSelector } from 'react-redux';
import { selectUserEmail } from '../../../slices/login.slice';
import { useHistory } from 'react-router-dom';
import { RECOVER_ACCOUNT_PATH, RESET_PASSWORD_VERIFY_EMAIL_PATH } from '../../../react-router-paths';
import { LoginLink } from '../../../shared/login-link/login-link';
import { LoadingDots } from '@workday/canvas-kit-react/loading-dots';
import { TertiaryButton, DeleteButton } from '@workday/canvas-kit-react/button';
import { useTranslation } from 'react-i18next';
import styles from './verify-authenticator.module.scss';

import { Modal } from '@workday/canvas-kit-react/modal';
import {
    usePopupModel,
    useInitialFocus,
    useReturnFocus,
    useFocusTrap,
    useAssistiveHideSiblings,
    useDisableBodyScroll
} from '@workday/canvas-kit-react/popup';
import { Flex } from '@workday/canvas-kit-react/layout';
import { useUniqueId } from '@workday/canvas-kit-react/common';
import { Checkbox } from '@workday/canvas-kit-react/checkbox';
import { autoId } from '@workday/upc-common';
import {
    ID_ACCEPT_RECOVER_ACCOUNT,
    ID_CANCEL_BUTTON,
    ID_CONFIRM_RECOVER_ACCOUNT,
    ID_RECOVER_ACCOUNT
} from '../../../autoIds';

export function VerifyAuthenticator() {
    const initialRenderRef = useRef(true);
    const { t } = useTranslation('auth');
    const userEmail = useSelector(selectUserEmail);
    const history = useHistory();
    const [verifyTotp, verifyTotpMutation] = useResetPasswordVerifyTotpMutation({
        fixedCacheKey: 'resetPasswordVerifyTotp'
    });
    const [recoverAccount, recoverAccountMutation] = useRecoverAccountMutation();
    const isVcrEnvironment = recoverAccountMutation.data?.isVcrEnvironment;

    const longDescId = useUniqueId();
    const cancelBtnRef = React.useRef(null);
    const model = usePopupModel({
        initialFocusRef: cancelBtnRef
    });
    // disable useCloseOnEscape and useCloseOnOverlayClick
    useInitialFocus(model);
    useReturnFocus(model);
    useFocusTrap(model);
    useAssistiveHideSiblings(model);
    useDisableBodyScroll(model);

    const onRecoverConfirmClick = () => {
        navigateToRecoverAccount();
    };

    useEffect(() => {
        if (initialRenderRef.current) {
            recoverAccount();
            initialRenderRef.current = false;
        }
    }, [recoverAccount]);

    useEffect(() => {
        if (verifyTotpMutation.isSuccess) {
            history.push(RESET_PASSWORD_VERIFY_EMAIL_PATH);
        }
    }, [history, verifyTotpMutation]);

    function onSubmit({ passcode }) {
        verifyTotp({ email: userEmail, passcode });
    }

    function onClick() {
        if (isVcrEnvironment) {
            navigateToRecoverAccount();
        } else {
            model.events.show();
        }
    }

    const [checked, setChecked] = React.useState(false);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setChecked(event.target.checked);
    };

    function navigateToRecoverAccount() {
        history.push(RECOVER_ACCOUNT_PATH);
    }

    return recoverAccountMutation.isSuccess || recoverAccountMutation.isError ? (
        <div>
            <TOTPVerificationForm
                onSubmit={onSubmit}
                disabled={verifyTotpMutation.isLoading}
                mutation={verifyTotpMutation}
            />
            <div className={styles.recoverContainer}>
                <span>{t('VerifyAuthenticator.noAuthenticator')}</span>
                <TertiaryButton onClick={onClick} size={'large'} {...autoId(ID_RECOVER_ACCOUNT)}>
                    {t('VerifyAuthenticator.recoverAccount')}
                </TertiaryButton>
            </div>

            <Modal model={model}>
                <Modal.Overlay>
                    <Modal.Card aria-describedby={longDescId}>
                        <Modal.Heading>{t('VerifyAuthenticator.recoverAccountModalTitle')}</Modal.Heading>
                        <Modal.Body>
                            <Flex as={'p'} id={longDescId} marginY={'zero'}>
                                {t('VerifyAuthenticator.recoverAccountParagraph1')}
                            </Flex>
                            <Flex as={'p'}>{t('VerifyAuthenticator.recoverAccountParagraph2')}</Flex>
                            <Flex marginTop={'l'}>
                                <Checkbox
                                    {...autoId(ID_ACCEPT_RECOVER_ACCOUNT)}
                                    checked={checked}
                                    label={t('VerifyAuthenticator.recoverAccountCheckbox')}
                                    onChange={handleChange}
                                />
                            </Flex>
                        </Modal.Body>
                        <Flex gap={'s'} padding={'xxs'} marginTop={'xxs'}>
                            <Modal.CloseButton ref={cancelBtnRef} {...autoId(ID_CANCEL_BUTTON)}>
                                {t('VerifyAuthenticator.recoverAccountCancelButton')}
                            </Modal.CloseButton>
                            <Modal.CloseButton
                                as={DeleteButton}
                                onClick={onRecoverConfirmClick}
                                disabled={!checked}
                                {...autoId(ID_CONFIRM_RECOVER_ACCOUNT)}>
                                {t('VerifyAuthenticator.recoverAccountResetButton')}
                            </Modal.CloseButton>
                        </Flex>
                    </Modal.Card>
                </Modal.Overlay>
            </Modal>
            <LoginLink />
        </div>
    ) : (
        <LoadingDots />
    );
}
